import React, {useEffect, useState} from 'react'
import {Autocomplete, Box, TextField} from '@mui/material'
import {useAuth} from '../../../auth/auth'
import {SearchUsersResult, UserSearchParams} from '../../utils'
import {H3User} from '../../../auth'

interface UserAutocompleteProps {
    searchParams: UserSearchParams | {}
    handleSelect: (value: SearchUsersResult | null) => void
    label: string | null
    requestedBy: SearchUsersResult | null
    searchPermission: boolean
}

function UserAutocomplete({searchParams, handleSelect, label, requestedBy, searchPermission}: UserAutocompleteProps) {
    const {searchUsers, getUser} = useAuth()
    const currentUser = getUser()
    const [options, setOptions] = useState<SearchUsersResult[]>([])
    const [value, setValue] = useState<SearchUsersResult | H3User | null>(requestedBy || currentUser || null)
    const [inputValue, setInputValue] = useState<string>(requestedBy ? `${requestedBy.full_name} (${requestedBy.user_id})` : '')
    const [error, setError] = useState<boolean>(false)

    useEffect(() => {
        if (searchPermission) {
            const fetchUsers = async () => {
                const users = await searchUsers(searchParams)
                const userIds = new Set(users.map(user => user.user_id))
                if (!userIds.has(currentUser.user_id)) {
                    users.push(currentUser)
                }
                setOptions(users)
            }

            fetchUsers()
        }
    }, [searchPermission, searchParams, searchUsers, currentUser])

    const handleChange = (_event: any, newValue: SearchUsersResult | null) => {
        setValue(newValue)
        setInputValue(newValue ? `${newValue.full_name} (${newValue.user_id})` : '')
        handleSelect(newValue)
    }

    return (
        <Box>
            <Autocomplete
                disablePortal
                options={options}
                getOptionLabel={(option) => `${option.full_name} (${option.user_id})`}
                isOptionEqualToValue={(option, value) => option.user_id === value?.user_id}
                value={value}
                onChange={handleChange}
                inputValue={inputValue}
                onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
                disabled={!searchPermission}
                renderInput={(params) => (
                    <TextField
                        required={searchPermission}
                        {...params}
                        label={label}
                        error={error}
                        helperText={error ? 'Please select a valid user' : ''}
                    />
                )}
            />
        </Box>
    )
}

export default UserAutocomplete
